/**
 * Scroll position manager to prevent unwanted refresh and improve mobile scroll experience
 */

// Store scroll positions by route path
const SCROLL_POSITIONS: Record<string, number> = {};
let isRestoringScroll = false;

/**
 * Save the current scroll position for a specific route
 */
export const saveScrollPosition = (path: string): void => {
  SCROLL_POSITIONS[path] = window.scrollY;
};

/**
 * Restore scroll position for a route with improved handling for mobile
 */
export const restoreScrollPosition = (path: string, options: { 
  delay?: number;
  force?: boolean;
} = {}): void => {
  const { delay = 0, force = false } = options;
  
  // Don't attempt restoration if we don't have a saved position
  // and we're not forcing scroll to top
  if (!force && !SCROLL_POSITIONS[path]) return;
  
  // Prevent scroll events during restoration
  isRestoringScroll = true;
  
  setTimeout(() => {
    // Use the stored position or go to top
    const scrollTarget = SCROLL_POSITIONS[path] || 0;
    
    window.scrollTo({
      top: scrollTarget,
      behavior: 'auto' // Use 'auto' for faster, more reliable scrolling
    });
    
    // Reset flag after a short delay to ensure scroll completed
    setTimeout(() => {
      isRestoringScroll = false;
    }, 100);
  }, delay);
};

/**
 * Check if we're currently in the process of programmatically restoring scroll
 */
export const isHandlingScroll = (): boolean => {
  return isRestoringScroll;
};

/**
 * Reset all saved scroll positions
 */
export const resetScrollPositions = (): void => {
  Object.keys(SCROLL_POSITIONS).forEach(key => {
    delete SCROLL_POSITIONS[key];
  });
};

/**
 * Enhanced scroll to top with debouncing for mobile
 */
export const scrollToTop = (options: {
  behavior?: ScrollBehavior;
  delay?: number;
} = {}): void => {
  const { behavior = 'auto', delay = 0 } = options;
  
  // Set flag to prevent interference from other scroll handlers
  isRestoringScroll = true;
  
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior,
    });
    
    // Reset flag after a short delay
    setTimeout(() => {
      isRestoringScroll = false;
    }, 100);
  }, delay);
};

/**
 * Prevent scroll momentum (for mobile)
 */
export const preventScrollMomentum = (): (() => void) => {
  // Store original overflow setting
  const originalStyle = document.body.style.overflow;
  
  // Set to hidden to stop momentum scrolling
  document.body.style.overflow = 'hidden';
  
  // Return cleanup function
  return () => {
    document.body.style.overflow = originalStyle;
  };
};

/**
 * Handle mobile touch events to improve scroll experience
 */
export const setupMobileTouchHandling = (): (() => void) => {
  let startY = 0;
  let lastY = 0;
  let isScrolling = false;
  
  const touchStartHandler = (e: TouchEvent) => {
    startY = e.touches[0].clientY;
    lastY = startY;
    isScrolling = false;
  };
  
  const touchMoveHandler = (e: TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - lastY;
    lastY = currentY;
    
    // Detect if user is actively scrolling
    if (Math.abs(deltaY) > 5) {
      isScrolling = true;
    }
    
    // Only prevent overscroll at bottom
    const scrollTop = window.scrollY;
    const scrollHeight = document.body.scrollHeight;
    const windowHeight = window.innerHeight;
    
    if (scrollTop + windowHeight >= scrollHeight && deltaY < 0) {
      // Only prevent at bottom when scrolling further down
      e.preventDefault();
    }
  };
  
  const touchEndHandler = () => {
    if (isScrolling) {
      isScrolling = false;
    }
  };
  
  document.addEventListener('touchstart', touchStartHandler, { passive: false });
  document.addEventListener('touchmove', touchMoveHandler, { passive: false });
  document.addEventListener('touchend', touchEndHandler);
  
  return () => {
    document.removeEventListener('touchstart', touchStartHandler);
    document.removeEventListener('touchmove', touchMoveHandler);
    document.removeEventListener('touchend', touchEndHandler);
  };
};

